import { ApolloError, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { GET_CLASH_COUNT } from 'api/graphQL/queries/bestPracticeClash.queries'

interface UseClashCountOptions {
    projectId: string | null
    treePath?: string | null
    skip?: boolean
}

interface UseClashCountResult {
    count: number
    loading: boolean
    error: ApolloError | undefined
    refetch: () => void
}

/**
 * Hook for fetching and managing Best Practice clash count
 * Used by ClashIconButton and ClashesPane components
 *
 * @param options - Configuration options for the query
 * @returns Clash count, loading state, and refetch function
 */
export const useClashCount = ({
    projectId,
    treePath,
    skip = false,
}: UseClashCountOptions): UseClashCountResult => {
    const [count, setCount] = useState(0)

    const { data, loading, error, refetch } = useQuery(GET_CLASH_COUNT, {
        variables: {
            input: {
                projectId,
                treePath,
            },
        },
        skip: !projectId || skip,
        fetchPolicy: 'cache-and-network',
    })

    useEffect(() => {
        if (data?.bestPracticeClashCount !== undefined) {
            setCount(data.bestPracticeClashCount)
        }
    }, [data])

    // Log error for debugging
    useEffect(() => {
        if (error) {
            console.error('Error fetching clash count:', error)
        }
    }, [error])

    return {
        count,
        loading,
        error,
        refetch,
    }
}
